<template>
  <div class="express-container">
    <!-- Skeleton Loader -->
    <div v-if="loading" class="skeleton-wrapper">
      <div class="skeleton-content">
        <div class="skeleton-slider">
          <!-- Skeleton tarjeta de bienvenida -->
          <div class="skeleton-card welcome-skeleton">
            <div class="skeleton-welcome-content">
              <div class="skeleton-icon"></div>
              <div class="skeleton-line title-line"></div>
              <div class="skeleton-line text-line"></div>
              <div class="skeleton-line highlight-line"></div>
            </div>
          </div>
          
          <!-- Skeleton tarjetas de noticias -->
          <div v-for="i in 7" :key="i" class="skeleton-card news-skeleton">
            <div class="skeleton-number"></div>
            <div class="skeleton-image"></div>
            <div class="skeleton-info">
              <div class="skeleton-category"></div>
              <div class="skeleton-line"></div>
              <div class="skeleton-line short"></div>
              <div class="skeleton-line extra-line"></div>
              <div class="skeleton-line extra-line"></div>
            </div>
          </div>
          
          <!-- Skeleton tarjeta bonus -->
          <div class="skeleton-card bonus-skeleton">
            <div class="skeleton-bonus-content">
              <div class="skeleton-icon"></div>
              <div class="skeleton-line bonus-title-line"></div>
              <div class="skeleton-line bonus-text-line"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Premium Check -->
    <div v-else-if="!isPremium" class="premium-blocked">
      <div class="premium-icon">🔒</div>
      <h2>Contenido Premium</h2>
      <p>Debes tener una suscripción activa para ver este contenido exclusivo de VISIONEXPRESS</p>
      <button class="subscribe-btn" @click="goToSubscription">Suscribete</button>
    </div>

    <!-- Contenido Express -->
    <div v-else class="express-content">
      <!-- Slider estilo Netflix -->
      <div class="slider-container">
        <div class="slider-track">
          <!-- Tarjeta de bienvenida (primer item) -->
          <div class="express-card welcome-card">
            <div class="welcome-content">
              <i class="material-icons welcome-timer">timer</i>
              <h1 class="welcome-title">¡Hola {{ userName }}!</h1>
              <p class="welcome-text">Esto es lo que tienes que saber hoy:</p>
              <p class="welcome-highlight">7 noticias explicadas en 5 minutos</p>
            </div>
          </div>

          <!-- Tarjetas de noticias (2 al 8) -->
          <div 
            v-for="(post, index) in posts.slice(0, 7)" 
            :key="post.id"
            class="express-card news-card"
            @click="goToPost(post)"
          >
            <div class="card-number">{{ index + 1 }}</div>
            <div class="card-content">
              <img :src="getImageUrl(post.image)" :alt="post.title" class="card-image">
              <div class="card-info">
                <span class="card-category">{{ post.category?.name || 'Express' }}</span>
                <h3 class="card-title">{{ post.title }}</h3>
                <p class="card-extract">{{ post.extract }}</p>
              </div>
            </div>
          </div>

          <!-- Tarjeta Bonus (noveno item) -->
          <div class="express-card bonus-card" @click="goToHome">
            <div class="bonus-content">
              <i class="material-icons timer-icon">timer</i>
              <h3 class="bonus-title">Hasta aquí la edición de hoy de VISIONEXPRESS</h3>
              <p class="bonus-text">Para mantenerte informado de toda la actualidad puedes leer JLHVISION</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '../../services/api'
import { useAuthStore } from '../../stores/auth'

const router = useRouter()
const authStore = useAuthStore()

const posts = ref([])
const loading = ref(true)
const isPremium = ref(false)
const userName = ref('Usuario')

const getImageUrl = (image) => {
  if (!image) return '/noimagen.png'
  if (image.startsWith('http')) return image
  return `https://portal.jlhvision.com/posts/${image}`
}

const loadExpressPosts = async () => {
  loading.value = true
  try {
    const response = await api.get('/premium/posts')
    posts.value = response.data.posts || []
    
    const user = authStore.user
    if (user && user.is_premium === true) {
      isPremium.value = true
      userName.value = user.first_name || user.full_name || 'Usuario'
    } else {
      isPremium.value = false
    }
  } catch (error) {
    console.error('Error cargando posts express:', error)
    posts.value = []
  } finally {
    loading.value = false
  }
}

const goToPost = (post) => {
  router.push(`/express/${post.slug}`)
}

const goToHome = () => {
  router.push('/home')
}

const goToSubscription = () => {
  router.push('/misuscripcion')
}

onMounted(() => {
  document.body.style.overflow = 'hidden'
  loadExpressPosts()
})

onUnmounted(() => {
  document.body.style.overflow = ''
})
</script>

<style scoped>
.express-container {
  position: fixed;
  top: 50px;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100vh;
  background: white;
  overflow: hidden;
}

/* ========== PREMIUM BLOCKED ========== */
.premium-blocked {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  text-align: center;
  color: #333;
  padding: 40px;
}

.premium-icon {
  font-size: 80px;
  margin-bottom: 20px;
}

.premium-blocked h2 {
  font-size: 28px;
  margin-bottom: 15px;
  color: #a50000;
}

.premium-blocked p {
  font-size: 16px;
  margin-bottom: 30px;
  opacity: 0.8;
  max-width: 400px;
}

.subscribe-btn {
  background: #a50000;
  color: white;
  border: none;
  padding: 14px 32px;
  font-size: 16px;
  font-weight: bold;
  border-radius: 40px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.subscribe-btn:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 15px rgba(165, 0, 0, 0.3);
}

.subscribe-btn:active {
  transform: scale(0.98);
}

/* ========== CONTENIDO CENTRADO ========== */
.express-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 70%;
  padding: 0 20px;
}

/* ========== SLIDER ========== */
.slider-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 15px;
  -webkit-overflow-scrolling: touch;
}

.slider-track {
  display: flex;
  gap: 16px;
  min-width: min-content;
  align-items: stretch;
}

.slider-container::-webkit-scrollbar {
  height: 4px;
}

.slider-container::-webkit-scrollbar-track {
  background: #e0e0e0;
  border-radius: 2px;
}

.slider-container::-webkit-scrollbar-thumb {
  background: #a50000;
  border-radius: 2px;
}

/* Tarjeta base */
.express-card {
  flex: 0 0 260px;
  background: white;
  border-radius: 10px;
  overflow: hidden;
  transition: transform 0.3s, box-shadow 0.3s;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
}

.express-card:hover {
  transform: scale(1.03);
  box-shadow: 0 8px 20px rgba(0,0,0,0.15);
}

/* ========== TARJETA DE BIENVENIDA ========== */
.welcome-card {
  background: white;
  border: 2px solid #a50000;
  cursor: default;
}

.welcome-card:hover {
  transform: none;
  box-shadow: none;
}

.welcome-content {
  padding: 25px 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex: 1;
}

.welcome-timer {
  font-size: 48px;
  color: #a50000;
  margin-bottom: 12px;
}

.welcome-title {
  font-size: 20px;
  font-weight: bold;
  color: #001b4a;
  margin-bottom: 10px;
}

.welcome-text {
  font-size: 13px;
  color: #333;
  margin-bottom: 5px;
}

.welcome-highlight {
  font-size: 14px;
  font-weight: bold;
  color: #001b4a;
  margin-top: 5px;
}

/* ========== TARJETAS DE NOTICIAS ========== */
.news-card {
  cursor: pointer;
  position: relative;
}

.card-number {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 32px;
  height: 32px;
  background: rgba(0,0,0,0.7);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  color: white;
  z-index: 2;
  backdrop-filter: blur(4px);
}

.card-image {
  width: 100%;
  height: 140px;
  object-fit: cover;
}

.card-info {
  padding: 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.card-category {
  display: inline-block;
  background: #a50000;
  color: white;
  font-size: 9px;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: uppercase;
  font-weight: bold;
  width: fit-content;
}

.card-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card-extract {
  font-size: 11px;
  color: #666;
  line-height: 1.45;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 10;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ========== TARJETA BONUS ========== */
.bonus-card {
  background: white;
  border: 2px solid #a50000;
  cursor: pointer;
}

.bonus-content {
  padding: 25px 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex: 1;
}

.timer-icon {
  font-size: 48px;
  color: #a50000;
  margin-bottom: 12px;
}

.bonus-title {
  font-size: 16px;
  font-weight: bold;
  color: #001b4a;
  margin-bottom: 10px;
  line-height: 1.3;
}

.bonus-text {
  font-size: 12px;
  color: #333;
  margin-bottom: 0;
  line-height: 1.4;
}

/* ========== SKELETON LOADER - MISMA ESTRUCTURA ========== */
.skeleton-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 70%;
  padding: 0 20px;
}

.skeleton-content {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

.skeleton-slider {
  display: flex;
  gap: 16px;
  min-width: min-content;
  align-items: stretch;
}

.skeleton-card {
  flex: 0 0 260px;
  background: #f5f5f5;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 6px rgba(0,0,0,0.05);
}

/* Skeleton tarjeta de bienvenida */
.welcome-skeleton {
  border: 2px solid #e0e0e0;
}

.skeleton-welcome-content {
  padding: 25px 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex: 1;
}

/* Skeleton tarjeta de noticias */
.news-skeleton {
  position: relative;
}

.skeleton-number {
  position: absolute;
  top: 8px;
  left: 8px;
  width: 32px;
  height: 32px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 6px;
}

.skeleton-image {
  width: 100%;
  height: 140px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-info {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.skeleton-category {
  width: 60px;
  height: 16px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 10px;
}

.skeleton-line {
  height: 14px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-line.short {
  width: 70%;
}

.skeleton-line.extra-line {
  width: 90%;
}

/* Skeleton tarjeta bonus */
.bonus-skeleton {
  border: 2px solid #e0e0e0;
}

.skeleton-bonus-content {
  padding: 25px 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex: 1;
  gap: 12px;
}

.skeleton-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 50%;
}

.title-line {
  width: 80%;
  height: 20px;
}

.text-line {
  width: 90%;
  height: 13px;
}

.highlight-line {
  width: 70%;
  height: 16px;
}

.bonus-title-line {
  width: 90%;
  height: 18px;
}

.bonus-text-line {
  width: 85%;
  height: 28px;
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .express-content,
  .skeleton-wrapper {
    height: 80%;
  }
  
  .express-card,
  .skeleton-card {
    flex: 0 0 240px;
  }
  
  .card-image,
  .skeleton-image {
    height: 130px;
  }
  
  .card-title {
    font-size: 13px;
  }
  
  .card-extract {
    font-size: 10px;
  }
  
  .welcome-title {
    font-size: 18px;
  }
  
  .welcome-text {
    font-size: 12px;
  }
  
  .welcome-highlight {
    font-size: 13px;
  }
  
  .welcome-timer,
  .timer-icon,
  .skeleton-icon {
    font-size: 40px;
  }
  
  .bonus-title {
    font-size: 14px;
  }
  
  .bonus-text {
    font-size: 11px;
  }
  
  .welcome-content,
  .bonus-content,
  .skeleton-welcome-content,
  .skeleton-bonus-content {
    padding: 20px 12px;
  }
}

@media (max-width: 480px) {
  .express-content,
  .skeleton-wrapper {
    height: 85%;
  }
  
  .express-card,
  .skeleton-card {
    flex: 0 0 220px;
  }
  
  .card-number,
  .skeleton-number {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }
  
  .card-info,
  .skeleton-info {
    padding: 10px;
  }
  
  .premium-blocked h2 {
    font-size: 22px;
  }
  
  .premium-blocked p {
    font-size: 14px;
  }
  
  .subscribe-btn {
    padding: 12px 28px;
    font-size: 14px;
  }
  
  .welcome-title {
    font-size: 16px;
  }
  
  .bonus-title {
    font-size: 13px;
  }
}
</style>